import React, { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import InfiniteScrollTrigger from "components/infinite-scroll/InfiniteScrollTrigger";
import SurveyCard from "components/card/SurveyCard";
import { skeletonGenerator } from "components/infinite-scroll/helpers";
import SurveySkeleton from "components/infinite-scroll/skeletons/SurveySkeleton";
import ListHeaderToolbar from "components/common/ListHeaderToolbar";
import { getSurveys } from "api-algolia/surveys.api";
import useSearch from "hooks/algolia/useSearch";
import { FiBarChart2, FiInbox, FiRotateCcw, FiDownloadCloud } from "react-icons/fi";

const parseTimestamp = (val) => {
  if (!val) return 0;
  if (typeof val === "number") return val;
  if (typeof val?.toMillis === "function") return val.toMillis();
  if (typeof val?.toDate === "function") return val.toDate().getTime();
  if (typeof val?._seconds === "number") {
    return val._seconds * 1000 + Math.round((val._nanoseconds || 0) / 1e6);
  }
  if (typeof val?.seconds === "number") {
    return val.seconds * 1000 + Math.round((val.nanoseconds || 0) / 1e6);
  }
  const parsed = Date.parse(val);
  return isNaN(parsed) ? 0 : parsed;
};

const SurveysList = ({
  surveys = [],
  totalCount,
  counts,
  currentUserId,
  currentUserDisplayName,
  currentUserType,
  isLoadingNextPage,
  hasMoreItems,
  getNextPage,
  page,
  setPage,
  createSurvey,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // 1. Search Hook
  const { setSearchText, searchText, searchResult, searchLoading } = useSearch({
    getData: getSurveys,
  });

  // 2. Filter States
  const [statusFilter, setStatusFilter] = useState("all"); // "all", "active", "finished"
  const [sortBy, setSortBy] = useState("newest"); // "newest", "oldest", "deadline"

  const isSearching = searchText.trim().length > 0;
  const baseItems = useMemo(
    () => (isSearching ? searchResult || [] : surveys),
    [isSearching, searchResult, surveys]
  );

  // 3. Metrics & Real Counts
  const stats = useMemo(() => {
    let localActive = 0;
    let localFinished = 0;

    surveys.forEach((s) => {
      if (s.type === "finished") localFinished++;
      else localActive++;
    });

    return {
      total: counts?.total || totalCount || surveys.length,
      active: counts?.active ?? localActive,
      finished: counts?.finished ?? localFinished,
      pending: 0,
    };
  }, [surveys, counts, totalCount]);

  // 4. Apply Filters and Sorting
  const filteredSurveys = useMemo(() => {
    let result = [...baseItems];

    // Status filter
    if (statusFilter === "active") {
      result = result.filter((s) => s.type !== "finished");
    } else if (statusFilter === "finished") {
      result = result.filter((s) => s.type === "finished");
    }

    // Sorting
    result.sort((a, b) => {
      if (sortBy === "oldest") {
        return parseTimestamp(a.createdAt) - parseTimestamp(b.createdAt);
      }
      if (sortBy === "deadline") {
        const da = parseTimestamp(a.deadline || a.endOfVoting);
        const db = parseTimestamp(b.deadline || b.endOfVoting);
        if (!da && !db) return 0;
        if (!da) return 1;
        if (!db) return -1;
        return da - db;
      }
      // default: newest
      return parseTimestamp(b.createdAt) - parseTimestamp(a.createdAt);
    });

    return result;
  }, [baseItems, statusFilter, sortBy]);

  const hasActiveFilters =
    isSearching || statusFilter !== "all" || sortBy !== "newest";

  const handleResetFilters = () => {
    setSearchText("");
    setStatusFilter("all");
    setSortBy("newest");
  };

  const handleCreate = async () => {
    const survey = await createSurvey({
      createdAt: Date.now(),
      author: {
        id: currentUserId,
        displayName: currentUserDisplayName,
      },
    });
    navigate(`/surveys/manage/${survey.id}`);
  };

  return (
    <div className="w-full min-h-screen bg-slate-50 p-6 sm:p-8 lg:p-10 overflow-y-auto">
      <div className="max-w-7xl mx-auto">
        {/* Enterprise Header & Filter Toolbar */}
        <ListHeaderToolbar
          title="Հարցումներ"
          icon={FiBarChart2}
          totalCount={stats.total}
          activeCount={stats.active}
          finishedCount={stats.finished}
          // Search
          searchText={searchText}
          setSearchText={setSearchText}
          searchPlaceholder="Որոնել հարցումներ ըստ վերնագրի կամ նկարագրության..."
          // Status
          statusFilter={statusFilter}
          setStatusFilter={setStatusFilter}
          statusOptions={[
            { id: "all", label: "Բոլորը" },
            { id: "active", label: "Ակտիվ" },
            { id: "finished", label: "Ավարտված" },
          ]}
          // Sort
          sortBy={sortBy}
          setSortBy={setSortBy}
          // Create Button
          canCreate={currentUserType === "admin" || currentUserType === "editor"}
          onCreateNew={handleCreate}
          createButtonText="Նոր հարցում"
          // Reset
          hasActiveFilters={hasActiveFilters}
          onResetFilters={handleResetFilters}
          filteredCount={filteredSurveys.length}
        />

        {/* Content Section */}
        {searchLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-3 gap-5 w-full">
            {skeletonGenerator(<SurveySkeleton />)}
          </div>
        ) : filteredSurveys.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-3 gap-5 w-full">
            {filteredSurveys.map((survey, index) => (
              <SurveyCard
                showTimers
                key={survey.id || index}
                item={survey}
                primaryButtonText={t("card.view")}
                secondaryButtonText={t("card.edit")}
                onCardClick={() => navigate(`/surveys/manage/${survey.id}`)}
              />
            ))}
          </div>
        ) : (
          /* Modern Empty State */
          <div className="bg-white border border-slate-200/80 rounded-2xl p-12 text-center max-w-lg mx-auto my-8 shadow-xs">
            <span className="w-14 h-14 rounded-2xl bg-indigo-50 text-indigo-600 border border-indigo-100 flex items-center justify-center mx-auto mb-4">
              <FiInbox size={26} />
            </span>
            <h3 className="text-base font-bold text-slate-900 mb-1">
              Հարցումներ չեն գտնվել
            </h3>
            <p className="text-xs text-slate-500 mb-5 leading-relaxed">
              {hasActiveFilters
                ? "Նշված ֆիլտրերին կամ որոնման հարցմանը համապատասխանող արդյունքներ չկան ներբեռնված ցանկում:"
                : "Համակարգում դեռևս չկան ավելացված հարցումներ:"}
            </p>
            <div className="flex items-center justify-center gap-3">
              {hasActiveFilters && (
                <button
                  type="button"
                  onClick={handleResetFilters}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-semibold bg-slate-100 hover:bg-slate-200 text-slate-700 transition cursor-pointer"
                >
                  <FiRotateCcw size={13} />
                  Մաքրել ֆիլտրերը
                </button>
              )}
              {hasMoreItems && !isSearching && (
                <button
                  type="button"
                  onClick={() => getNextPage && getNextPage()}
                  disabled={isLoadingNextPage}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-semibold bg-indigo-600 hover:bg-indigo-700 text-white transition cursor-pointer disabled:opacity-50"
                >
                  <FiDownloadCloud size={13} />
                  {isLoadingNextPage ? "Բեռնվում է..." : "Բեռնել ավելին սերվերից"}
                </button>
              )}
            </div>
          </div>
        )}

        {/* Load More Button & Infinite Scroll */}
        {!isSearching && (
          <div className="mt-4 flex flex-col items-center justify-center gap-3">
            {isLoadingNextPage && (
              <div className="grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-3 gap-5 w-full">
                {skeletonGenerator(<SurveySkeleton />)}
              </div>
            )}

            {hasMoreItems && !isLoadingNextPage && (
              <button
                type="button"
                onClick={() => getNextPage && getNextPage()}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 text-xs font-semibold shadow-xs transition hover:shadow-sm cursor-pointer"
              >
                <FiDownloadCloud size={15} />
                Բեռնել հաջորդ էջը
              </button>
            )}

            <InfiniteScrollTrigger
              isLoading={isLoadingNextPage}
              hasMoreItems={hasMoreItems}
              callback={getNextPage}
              page={page}
              setPage={setPage}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default SurveysList;
